import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { UserCog, Users, Plus, Trash2, Eye, ShieldCheck, Mail, Crown, Send } from "lucide-react";
import api from "../lib/api";
import { useToast } from "../hooks/use-toast";

const AccessBadge = ({ access }) => (
  access === "action"
    ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-700"><ShieldCheck className="h-3 w-3" /> Action taker</span>
    : <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600"><Eye className="h-3 w-3" /> View only</span>
);

const EditClientModal = ({ open, onClose, client, onUpdated, mode = "admin" }) => {
  const { toast } = useToast();
  const isAdmin = mode === "admin";
  const [form, setForm] = useState({ name: "", nature_of_engagement: "", period: "", employee_name: "", employee_email: "", client_contact_email: "" });
  const [contacts, setContacts] = useState([]);
  const [members, setMembers] = useState([]);
  const [newContact, setNewContact] = useState({ name: "", email: "", is_spoc: false });
  const [newMember, setNewMember] = useState({ name: "", email: "", access: "view" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (client) {
      setForm({
        name: client.name || "", nature_of_engagement: client.nature_of_engagement || "", period: client.period || "",
        employee_name: client.employee_name || "", employee_email: client.employee_email || "",
        client_contact_email: client.client_contact_email || "",
      });
      setContacts(client.contacts || []);
      setMembers(client.members || []);
      setNewContact({ name: "", email: "", is_spoc: false });
      setNewMember({ name: "", email: "", access: "view" });
    }
  }, [client]);

  const saveDetails = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put(`/clients/${client.id}`, form);
      toast({ title: "Client updated", description: data.reassigned ? "Handler reassigned & notified." : "Details saved." });
      onUpdated && onUpdated();
      onClose();
    } catch (err) {
      toast({ title: "Update failed", description: err?.response?.data?.detail || "Try again.", variant: "destructive" });
    } finally { setBusy(false); }
  };

  const addContact = async () => {
    if (!newContact.email.trim()) { toast({ title: "Enter an email", variant: "destructive" }); return; }
    try {
      const { data } = await api.post(`/clients/${client.id}/contacts`, newContact);
      setContacts(data.contacts);
      setNewContact({ name: "", email: "", is_spoc: false });
      toast({ title: "Contact added", description: `Invite sent to ${newContact.email}` });
      onUpdated && onUpdated();
    } catch (err) { toast({ title: "Could not add contact", description: err?.response?.data?.detail || "Try again.", variant: "destructive" }); }
  };
  const removeContact = async (uid) => {
    try { const { data } = await api.delete(`/clients/${client.id}/contacts/${uid}`); setContacts(data.contacts); toast({ title: "Contact removed" }); onUpdated && onUpdated(); }
    catch (err) { toast({ title: "Remove failed", description: err?.response?.data?.detail || "Try again.", variant: "destructive" }); }
  };
  const makeSpoc = async (uid, rank = 1) => {
    try { const { data } = await api.post(`/clients/${client.id}/contacts/${uid}/spoc?rank=${rank}`); setContacts(data.contacts); toast({ title: rank === 2 ? "SPOC 2 updated" : "SPOC 1 (main) updated" }); onUpdated && onUpdated(); }
    catch (err) { toast({ title: "Update failed", variant: "destructive" }); }
  };

  const addMember = async () => {
    if (!newMember.email.trim()) { toast({ title: "Enter an email", variant: "destructive" }); return; }
    try {
      const { data } = await api.post(`/clients/${client.id}/members`, newMember);
      setMembers(data.members);
      setNewMember({ name: "", email: "", access: "view" });
      if (data.pending) toast({ title: "Sent for approval", description: "Your firm admin must approve adding this member." });
      else toast({ title: "Member added", description: data.invite ? `Invite sent to ${newMember.email}` : "Member added." });
      onUpdated && onUpdated();
    } catch (err) { toast({ title: "Could not add member", description: err?.response?.data?.detail || "Try again.", variant: "destructive" }); }
  };
  const removeMember = async (uid) => {
    try { const { data } = await api.delete(`/clients/${client.id}/members/${uid}`); setMembers(data.members); toast(data.pending ? { title: "Sent for approval", description: "Admin approval is required to remove this member." } : { title: "Member removed" }); onUpdated && onUpdated(); }
    catch (err) { toast({ title: "Remove failed", description: err?.response?.data?.detail || "Try again.", variant: "destructive" }); }
  };
  const changeMemberAccess = async (uid, access) => {
    try { const { data } = await api.put(`/clients/${client.id}/members/${uid}`, { access }); setMembers(data.members); toast(data.pending ? { title: "Sent for approval", description: "Admin approval is required to change this access level." } : { title: "Access updated", description: access === "action" ? "Action taker" : "View only" }); onUpdated && onUpdated(); }
    catch (err) { toast({ title: "Update failed", description: err?.response?.data?.detail || "Try again.", variant: "destructive" }); }
  };
  const requestSelfAccess = async () => {
    try { await api.post(`/clients/${client.id}/request-access`); toast({ title: "Request sent", description: "Your firm admin will review your request for action access." }); onUpdated && onUpdated(); }
    catch (err) { toast({ title: "Could not send request", description: err?.response?.data?.detail || "Try again.", variant: "destructive" }); }
  };
  const resendInvite = async (email) => {
    try { await api.post(`/invites/resend`, { email }); toast({ title: "Invite sent", description: `A fresh sign-in link was emailed to ${email}` }); }
    catch (err) { toast({ title: "Could not resend", description: err?.response?.data?.detail || "Try again.", variant: "destructive" }); }
  };

  if (!client) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-client-modal" className="sm:max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50"><UserCog className="h-6 w-6 text-indigo-700" /></div>
          <DialogTitle className="text-2xl font-bold text-[#1a1a2e]">Edit {client.name}</DialogTitle>
          <DialogDescription className="text-slate-500">Manage the client's contacts, additional access{isAdmin ? ", engagement details and handling staff" : ""}.</DialogDescription>
        </DialogHeader>

        {isAdmin && (
          <form onSubmit={saveDetails} className="mt-2 space-y-3">
            <div className="space-y-1.5"><Label>Client name</Label><Input data-testid="edit-client-name" required value={form.name} onChange={set("name")} className="rounded-xl" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Nature of engagement</Label><Input required value={form.nature_of_engagement} onChange={set("nature_of_engagement")} className="rounded-xl" /></div>
              <div className="space-y-1.5"><Label>Period</Label><Input required value={form.period} onChange={set("period")} className="rounded-xl" /></div>
            </div>
            <div className="rounded-xl bg-slate-50 p-4 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Handling staff (reassign)</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Name</Label><Input data-testid="edit-client-handler-name" required value={form.employee_name} onChange={set("employee_name")} className="rounded-xl bg-white" /></div>
                <div className="space-y-1.5"><Label>Email</Label><Input data-testid="edit-client-handler-email" required type="email" value={form.employee_email} onChange={set("employee_email")} className="rounded-xl bg-white" /></div>
              </div>
              <p className="text-xs text-slate-400">Changing the email reassigns all this client's requests to the new handler and notifies them.</p>
            </div>
            <Button data-testid="edit-client-save" type="submit" disabled={busy} className="w-full rounded-xl bg-[#2f2b8f] py-5 text-sm font-semibold text-white hover:bg-[#26236f]">
              {busy ? "Saving..." : "Save details"}
            </Button>
          </form>
        )}

        {/* Client contacts */}
        <div className={`${isAdmin ? "mt-2 border-t border-slate-100 pt-4" : "mt-2"}`}>
          <h3 className="flex items-center gap-2 font-bold text-[#1a1a2e]"><Mail className="h-4 w-4" /> Client contacts</h3>
          <p className="mt-1 text-xs text-slate-500">People on the client side who can log in and upload. The SPOC can see & act on all requests for this client; others see only requests sent to them.</p>
          <div className="mt-3 space-y-2">
            {contacts.map((ct) => (
              <div key={ct.user_id} data-testid={`contact-row-${ct.email}`} className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm font-medium text-[#1a1a2e]">{ct.name || ct.email}
                    {(ct.spoc_rank === 1 || (ct.is_spoc && !ct.spoc_rank)) && <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold text-indigo-700"><Crown className="h-2.5 w-2.5" /> SPOC 1</span>}
                    {ct.spoc_rank === 2 && <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-700"><Crown className="h-2.5 w-2.5" /> SPOC 2</span>}
                  </div>
                  <div className="truncate text-xs text-slate-500">{ct.email}</div>
                </div>
                <div className="flex items-center gap-1">
                  {!(ct.spoc_rank === 1 || (ct.is_spoc && !ct.spoc_rank)) && <button data-testid={`make-spoc1-${ct.email}`} onClick={() => makeSpoc(ct.user_id, 1)} title="Set as SPOC 1 (main)" className="rounded-lg px-1.5 py-1 text-[10px] font-bold text-slate-400 hover:bg-indigo-50 hover:text-indigo-600">SPOC 1</button>}
                  {ct.spoc_rank !== 2 && <button data-testid={`make-spoc2-${ct.email}`} onClick={() => makeSpoc(ct.user_id, 2)} title="Set as SPOC 2" className="rounded-lg px-1.5 py-1 text-[10px] font-bold text-slate-400 hover:bg-sky-50 hover:text-sky-600">SPOC 2</button>}
                  <button data-testid={`resend-contact-${ct.email}`} onClick={() => resendInvite(ct.email)} title="Resend invite link" className="rounded-lg p-1.5 text-slate-400 hover:bg-emerald-50 hover:text-emerald-600"><Send className="h-4 w-4" /></button>
                  {contacts.length > 1 && <button onClick={() => removeContact(ct.user_id)} title="Remove" className="rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 space-y-2 rounded-xl border border-dashed border-slate-200 p-3">
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="contact-name-input" value={newContact.name} onChange={(e) => setNewContact({ ...newContact, name: e.target.value })} placeholder="Name" className="rounded-xl" />
              <Input data-testid="contact-email-input" type="email" value={newContact.email} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })} placeholder="Email" className="rounded-xl" />
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-xs text-slate-600"><Checkbox checked={newContact.is_spoc} onCheckedChange={(v) => setNewContact({ ...newContact, is_spoc: !!v })} /> Make this contact the SPOC</label>
              <Button data-testid="add-contact-btn" onClick={addContact} className="rounded-xl bg-[#2f2b8f] text-white hover:bg-[#26236f]"><Plus className="mr-1 h-4 w-4" /> Add</Button>
            </div>
          </div>
        </div>

        {/* Firm-side members */}
        <div className="mt-4 border-t border-slate-100 pt-4">
          <h3 className="flex items-center gap-2 font-bold text-[#1a1a2e]"><Users className="h-4 w-4" /> Additional firm access</h3>
          <p className="mt-1 text-xs text-slate-500">Give other firm members view-only or action access to this client.</p>
          {!isAdmin && (
            <p className="mt-1 text-xs text-amber-600">Changes you make here require your firm admin's approval before they take effect.</p>
          )}
          {!isAdmin && client?.my_access === "view" && (
            <div data-testid="self-access-banner" className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-indigo-100 bg-indigo-50/60 px-4 py-3">
              <div className="flex items-center gap-2 text-sm text-indigo-800">
                <Eye className="h-4 w-4" /> You currently have <strong>view-only</strong> access to this client.
              </div>
              <Button data-testid="request-action-access-btn" onClick={requestSelfAccess} size="sm" className="rounded-xl bg-[#2f2b8f] text-white hover:bg-[#26236f]"><ShieldCheck className="mr-1.5 h-3.5 w-3.5" /> Request action access</Button>
            </div>
          )}
          <div className="mt-3 space-y-2">
            {members.length === 0 && <p className="text-sm text-slate-400">No additional members yet.</p>}
            {members.map((m) => (
              <div key={m.user_id} data-testid={`member-row-${m.email}`} className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5">
                <div className="min-w-0"><div className="text-sm font-medium text-[#1a1a2e]">{m.name || m.email}</div><div className="truncate text-xs text-slate-500">{m.email}</div></div>
                <div className="flex items-center gap-2">
                  <Select value={m.access === "action" ? "action" : "view"} onValueChange={(v) => changeMemberAccess(m.user_id, v)}>
                    <SelectTrigger data-testid={`member-access-${m.email}`} className="h-8 w-[130px] rounded-lg text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="view">View only</SelectItem>
                      <SelectItem value="action">Action taker</SelectItem>
                    </SelectContent>
                  </Select>
                  <button data-testid={`resend-member-${m.email}`} onClick={() => resendInvite(m.email)} title="Resend invite link" className="rounded-lg p-1.5 text-slate-400 hover:bg-emerald-50 hover:text-emerald-600"><Send className="h-4 w-4" /></button>
                  <button onClick={() => removeMember(m.user_id)} title="Remove" className="rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 space-y-2 rounded-xl border border-dashed border-slate-200 p-3">
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="member-name-input" value={newMember.name} onChange={(e) => setNewMember({ ...newMember, name: e.target.value })} placeholder="Name" className="rounded-xl" />
              <Input data-testid="member-email-input" type="email" value={newMember.email} onChange={(e) => setNewMember({ ...newMember, email: e.target.value })} placeholder="Email" className="rounded-xl" />
            </div>
            <div className="flex items-center gap-2">
              <Select value={newMember.access} onValueChange={(v) => setNewMember({ ...newMember, access: v })}>
                <SelectTrigger data-testid="member-access-trigger" className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="view">Firm participant — view only</SelectItem>
                  <SelectItem value="action">Action taker — create/edit/review</SelectItem>
                </SelectContent>
              </Select>
              <Button data-testid="add-member-btn" onClick={addMember} className="rounded-xl bg-[#2f2b8f] text-white hover:bg-[#26236f]"><Plus className="mr-1 h-4 w-4" /> Add</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditClientModal;
